import { useState } from "react"
import Header from "@/components/Header"
import Footer from "@/components/Footer"
import {Calendar, Star, Sparkles} from 'lucide-react'
import { birthstones } from "@/utils/gemstone"

function BirthstoneCalculator() {
  const [birthDate, setBirthDate] = useState("")
  const [result, setResult] = useState<{ month: number; stone: string; vedic: string } | null>(null)

  const handleCalculate = () => {
    if (!birthDate) return

    const date = new Date(birthDate)
    const month = date.getMonth() + 1 // JavaScript months are 0-indexed

    const birthstoneData = birthstones[month]
    setResult({
      month,
      stone: birthstoneData.stone,
      vedic: birthstoneData.vedic,
    })
  }

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ]

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-amber-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            Birthstone & Vedic Gemstone Calculator
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Find your lucky stone based on your date of birth. Discover both traditional birthstones and Vedic astrology gemstone recommendations.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl border border-amber-100 overflow-hidden mb-12">
            <div className="bg-gradient-to-r from-amber-500 to-orange-600 px-8 py-6">
              <div className="flex items-center gap-3">
                <Calendar className="w-8 h-8 text-white" />
                <div>
                  <h2 className="text-2xl font-bold text-white">
                    Find Your Birthstone
                  </h2>
                  <p className="text-amber-50 text-sm mt-1">
                    Enter your birth date to discover your lucky gemstones
                  </p>
                </div>
              </div>
            </div>

            <div className="p-8">
              <div className="max-w-md mx-auto space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Your Birth Date
                  </label>
                  <input
                    type="date"
                    value={birthDate}
                    onChange={(e) => setBirthDate(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none transition-colors"
                  />
                </div>

                <button
                  onClick={handleCalculate}
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-600 text-white py-4 rounded-xl font-semibold hover:shadow-lg transform hover:-translate-y-0.5 transition-all"
                >
                  Find My Lucky Stone
                </button>
              </div>

              {result && (
                <div className="mt-8 space-y-6">
                  <div className="text-center">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      Your Birth Month: {monthNames[result.month - 1]}
                    </h3>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-gradient-to-br from-blue-50 to-emerald-50 rounded-xl p-6 border-2 border-blue-100">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                          <Star className="w-6 h-6 text-white" />
                        </div>
                        <h4 className="text-xl font-bold text-gray-900">Traditional Birthstone</h4>
                      </div>
                      <p className="text-3xl font-bold text-gradient-sapphire mb-2">
                        {result.stone}
                      </p>
                      <p className="text-sm text-gray-600">
                        Your traditional Western birthstone, associated with your birth month and believed to bring good fortune.
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-6 border-2 border-orange-100">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
                          <Sparkles className="w-6 h-6 text-white" />
                        </div>
                        <h4 className="text-xl font-bold text-gray-900">Vedic Gemstone</h4>
                      </div>
                      <p className="text-3xl font-bold text-gradient-gold mb-2">
                        {result.vedic}
                      </p>
                      <p className="text-sm text-gray-600">
                        Your Vedic astrology gemstone recommendation, used in Eastern traditions for spiritual and healing properties.
                      </p>
                    </div>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                    <p className="text-sm text-gray-700">
                      <strong>Note:</strong> Both traditional and Vedic gemstones are believed to bring positive energy and fortune. 
                      Consider these recommendations when choosing jewelry or gemstones for personal use or gifting.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Birthstone Reference Table */}
          <div className="bg-white rounded-2xl shadow-xl border border-amber-100 p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
              Complete Birthstone Reference
            </h2>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-3 px-4 font-bold text-gray-900">Month</th>
                    <th className="text-left py-3 px-4 font-bold text-gray-900">Traditional Birthstone</th>
                    <th className="text-left py-3 px-4 font-bold text-gray-900">Vedic Gemstone</th>
                  </tr>
                </thead>
                <tbody>
                  {monthNames.map((month, index) => {
                    const data = birthstones[index + 1]
                    return (
                      <tr key={month} className="border-b border-gray-100 hover:bg-amber-50 transition-colors">
                        <td className="py-3 px-4 font-semibold text-gray-900">{month}</td>
                        <td className="py-3 px-4 text-gray-700">{data.stone}</td>
                        <td className="py-3 px-4 text-gray-700">{data.vedic}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}

export default BirthstoneCalculator
